import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { FeatureFlag } from '@mews-distributor/common';
import { getActiveFeatureFlags } from '@mews/framework/feature-flags';
import { createUUID } from '@mews/std';
import { DeeplinkParameters } from '../../deeplinks';
import { getCurrencyCode, getLanguageCode, isFlexibleRatesFilterEnabledQuery } from '../../modules/app/selectors';
import { getVersion, isStandalone } from '../../modules/configuration/selectors';
import { getEnterprise, getEnterpriseDefaultLanguageCode } from '../../modules/entities/enterprises/selectors';
import { localizeProperty } from '../../modules/globalization/selectors';
import { getSelectedHotelId } from '../../modules/resourceOrder/selectors';
import { DistributorPromiseThunkAction, DistributorThunkAction } from '../../types';

export const initializeGainsightService =
    (): DistributorPromiseThunkAction =>
    async (_dispatch, _getState, { gainsightService, loggerService }) => {
        try {
            await gainsightService.initialize();
        } catch (error) {
            loggerService.log('gainsight-service-init-error', 'Gainsight service could not be initialized.', {
                message: error.message,
            });
        }
    };

export const setGainsightUser =
    (): DistributorThunkAction =>
    (_dispatch, getState, { gainsightService }) => {
        const state = getState();
        const enterpriseId = getSelectedHotelId(state);

        if (enterpriseId == null) {
            return;
        }

        gainsightService.setUser({
            id: createUUID(),
            signUpDate: null,
            enterpriseId,
        });
    };

export const setInitialGainsightServiceGlobalContext =
    (): DistributorThunkAction =>
    (_dispatch, getState, { gainsightService, featureFlagService }) => {
        const state = getState();
        const queryString = new URLSearchParams(window.location.search);
        const featureFlagsActive = getActiveFeatureFlags({ featureFlagService, featureFlagEnum: FeatureFlag });
        const deeplinksUsed = pipe(
            Object.keys(DeeplinkParameters),
            array.filter((link) => queryString.get(link) != null)
        );

        const enterpriseId = getSelectedHotelId(state);
        const enterpriseDefaultLanguage =
            enterpriseId != null ? getEnterpriseDefaultLanguageCode(state, { enterpriseId }) : undefined;
        const enterprise = enterpriseId != null ? getEnterprise(state, { enterpriseId }) : undefined;
        const bookingEngineId = enterprise?.configurationId;
        const enterpriseName = localizeProperty(state, {
            property: enterprise?.name,
            languageCode: enterpriseDefaultLanguage,
        });

        gainsightService.setGlobalContext({
            featureFlagsActive,
            integrationType: isStandalone(state) ? 'Standalone' : 'Widget',
            applicationVersion: getVersion(),
            languageDisplayed: getLanguageCode(state),
            currencyDisplayed: getCurrencyCode(state),
            resolutionWidth: window.screen.width,
            resolutionHeight: window.screen.height,
            flexibleRatesState: isFlexibleRatesFilterEnabledQuery(state) ?? false,
            deeplinksUsed,
            bookingEngineId,
            enterpriseName,
        });
    };
